import { Input } from 'antd'
import moment from 'moment'
import { ComponentProps, useCallback, useEffect, useState } from 'react'

import { useContainerSize } from '@/hooks/useContainerSize'
import cn from '@/utils/tailwind/cn'

import BaseField from '../BaseField'
import { IndependentDatetimePicker } from '.'
import { RawIndependentDatePicker } from './RawIndependentDatePicker'
import { RawIndependentTimePicker } from './RawIndependentTimePicker'

type Props = ComponentProps<typeof IndependentDatetimePicker>

export const RawIndependentDatetimePicker = ({
  id,
  name,
  onBlur,
  initialValue,
  value,
  onChange,
  minDate,
  maxDate,
  disabled,
  placeholder,
  className,
  readonly,
  showPastOnly,
  showFutureOnly,
  ...props
}: Props) => {
  const { width: fatherWidth, containerRef } = useContainerSize()

  const getInitialDate = (value: Props['initialValue']) => {
    if (!value) return undefined
    const date = moment(value)
    return date.isValid() ? date.format('YYYY-MM-DD') : undefined
  }

  const getInitialTime = (value: Props['initialValue']) => {
    if (!value) return undefined
    const date = moment(value)
    return date.isValid() ? date.format('HH:mm:ss') : undefined
  }

  const [date, setDate] = useState<string | undefined>(() =>
    getInitialDate(initialValue || value)
  )
  const [time, setTime] = useState<string | undefined>(() =>
    getInitialTime(initialValue || value)
  )

  const isSmaller = fatherWidth <= 380

  useEffect(() => {
    if (value) {
      const newDate = getInitialDate(value)
      const newTime = getInitialTime(value)
      setDate(newDate)
      setTime(newTime)
    }
  }, [value])

  const readOnlyValue =
    readonly && date && time
      ? moment(`${date}T${time}`).format('MM/DD/YYYY hh:mm A')
      : undefined

  const handleChange = useCallback(
    (newDate: string | undefined, newTime: string | undefined) => {
      if (newDate && newTime) {
        const dateTime = moment(`${newDate}T${newTime}`)
        if (dateTime.isValid()) {
          onChange?.(dateTime.format('YYYY-MM-DD[T]HH:mm:ss'))
        }
      }
    },
    [onChange]
  )

  const handleDateChange = useCallback(
    (value: string | undefined) => {
      setDate(value)
      handleChange(value, time)
    },
    [time, handleChange]
  )

  const handleTimeChange = useCallback(
    (value: string | undefined) => {
      setTime(value)
      handleChange(date, value)
    },
    [date, handleChange]
  )

  return (
    <BaseField {...props}>
      {readonly ? (
        <Input
          readOnly
          className="pointer-events-none"
          placeholder="MM/DD/YYYY hh:mm A"
          value={readOnlyValue}
        />
      ) : (
        <div
          className={cn(
            'grid w-full grid-cols-[1fr_107px] gap-2',
            {
              'grid-cols-1 grid-rows-2': isSmaller,
            },
            className
          )}
          ref={containerRef}
        >
          <RawIndependentDatePicker
            onChange={handleDateChange}
            value={date}
            {...{
              onBlur,
              minDate,
              maxDate,
              disabled,
              className,
              showPastOnly,
              showFutureOnly,
              fatherWidth: fatherWidth - 120,
            }}
          />

          <RawIndependentTimePicker
            onChange={handleTimeChange}
            value={time}
            {...{ onBlur, className, disabled }}
          />
        </div>
      )}
    </BaseField>
  )
}
